/* ============================================================
   The Stripe-style split-screen checkout, as a component.

   Left: what is being bought and what it costs, branded and persistent.
   Right: the pay button, then a skeleton while Paystack's secure window
   opens over the top of it.

   Why a component: PublicOnboarding grew this layout for school signup,
   and Trasse Families needs the same two-panel handoff for the day-15
   subscription. The colours, rows and copy are props so a violet family
   billing page and a blue school checkout can share one implementation
   without either pretending to be the other.

   PublicOnboarding still renders its own copy of this layout verbatim —
   this file does not change it. Anything moved here later must keep that
   screen pixel-identical.
   ============================================================ */
import React from 'react';
import { FaCreditCard, FaLock, FaPhoneAlt } from 'react-icons/fa';
import { CheckoutResult, loadPaystackInline } from '../services/paymentsApi';

export interface PaySummaryRow {
  label: string;
  value: React.ReactNode;
  faint?: boolean;
}

export interface PaystackSplitScreenProps {
  /** Small caps label over the amount, e.g. "Amount due". */
  eyebrow: string;
  /** The headline figure, already formatted ("Ksh 1,500"). */
  amount: string;
  /** One line under the amount — cadence, first-charge date, whatever matters. */
  amountNote?: React.ReactNode;
  rows: PaySummaryRow[];
  /** Right pane heading + explanation. */
  payTitle?: string;
  payBlurb?: React.ReactNode;
  ctaLabel: string;
  onPay: () => void;
  /** True while the secure window is being opened — swaps the CTA for a skeleton. */
  busy: boolean;
  onBack?: () => void;
  backLabel?: string;
  /** Rendered under the CTA (cancellation policy, M-Pesa explanation, …). */
  footnote?: React.ReactNode;
  /** Anything extra in the left column, under the rows. */
  aside?: React.ReactNode;
  accent?: string;
  accentGradient?: string;
}

/**
 * Open Paystack over the current page, falling back to the hosted page.
 *
 * The amount is ALWAYS the one the server set (we resume the initialized
 * transaction by access_code — Paystack's plain inline embed takes a
 * client-side amount, which must never be trusted). When inline can't load we
 * hand the browser to the hosted URL, which returns to /billing/return.
 */
export async function runPaystackCheckout(
  res: CheckoutResult,
  handlers: { onSuccess: () => void; onDismiss: () => void; onError?: (msg: string) => void },
): Promise<void> {
  const PaystackPop = res.access_code ? await loadPaystackInline() : null;
  if (PaystackPop) {
    try {
      new PaystackPop().resumeTransaction(res.access_code, {
        onSuccess: handlers.onSuccess,
        onCancel: handlers.onDismiss,
        onError: (err: { message?: string }) => {
          handlers.onDismiss();
          handlers.onError?.(err?.message || 'please try again');
        },
      });
      return;
    } catch (modalErr) {
      // eslint-disable-next-line no-console
      console.warn('[paystack] modal checkout failed, using hosted redirect:', modalErr);
    }
  }
  window.location.href = res.authorization_url;
}

const DEFAULT_ACCENT = '#6f4ff0';
const DEFAULT_GRADIENT = 'linear-gradient(135deg,#9b7cff,#6f4ff0)';

export default function PaystackSplitScreen({
  eyebrow, amount, amountNote, rows, payTitle = 'Payment', payBlurb, ctaLabel,
  onPay, busy, onBack, backLabel = 'Back', footnote, aside,
  accent = DEFAULT_ACCENT, accentGradient = DEFAULT_GRADIENT,
}: PaystackSplitScreenProps) {
  const skelBar = (w: string) => <div className="pss-skel" style={{ height: 44, width: w, borderRadius: 10 }} />;

  return (
    <div className="pss-root" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#fff' }}>
      <style>{`
        @keyframes pssShimmer { 0% { background-position: -420px 0 } 100% { background-position: 420px 0 } }
        .pss-skel { background: linear-gradient(90deg,#eef1f6 25%,#e2e7f0 37%,#eef1f6 63%); background-size: 840px 100%; animation: pssShimmer 1.25s ease-in-out infinite; }
        .pss-split { display: grid; grid-template-columns: 1fr 1fr; flex: 1; min-height: 0; }
        @media (max-width: 860px) { .pss-split { grid-template-columns: 1fr; } .pss-split .pss-left { border-right: none !important; border-bottom: 1px solid #ececf3; } }
        .pss-cta { width: 100%; border: none; border-radius: 12px; padding: 15px 18px; font: inherit; font-size: 15.5px; font-weight: 800; color: #fff; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px; }
        .pss-cta:disabled { opacity: .55; cursor: default; }
      `}</style>
      <div className="pss-split">
        {/* LEFT — the summary */}
        <div className="pss-left" style={{ background: 'linear-gradient(180deg,#faf8ff,#f4f0fd)', borderRight: '1px solid #ececf3', padding: '46px clamp(20px,5vw,64px)' }}>
          <div style={{ maxWidth: 440, marginLeft: 'auto', width: '100%' }}>
            {onBack && (
              <button type="button" onClick={onBack}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'none', border: 'none', color: '#5a5470', fontSize: 13.5, fontWeight: 600, cursor: 'pointer', padding: 0, marginBottom: 26 }}>
                ← {backLabel}
              </button>
            )}
            <div style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#9a93ae' }}>{eyebrow}</div>
            <div style={{ fontSize: 'clamp(36px,6vw,52px)', fontWeight: 800, letterSpacing: '-.02em', color: '#1b1533', lineHeight: 1.02, margin: '6px 0 4px' }}>{amount}</div>
            {amountNote && <div style={{ fontSize: 14, color: '#5a5470' }}>{amountNote}</div>}

            <div style={{ marginTop: 26 }}>
              {rows.map((r, i) => (
                <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '12px 0', borderTop: i === 0 ? 'none' : '1px solid #ececf3', fontSize: 14 }}>
                  <span style={{ color: '#9a93ae' }}>{r.label}</span>
                  <span style={{ color: r.faint ? '#9a93ae' : '#1b1533', fontWeight: r.faint ? 500 : 600, textAlign: 'right' }}>{r.value}</span>
                </div>
              ))}
            </div>

            {aside}

            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 22, color: '#9a93ae', fontSize: 12.5, flexWrap: 'wrap' }}>
              <FaLock style={{ fontSize: 11 }} /> Secured by Paystack
              <span style={{ color: '#d8d2e6' }}>·</span>
              <FaCreditCard style={{ fontSize: 12 }} /> Card
              <span style={{ color: '#d8d2e6' }}>·</span>
              <FaPhoneAlt style={{ fontSize: 11 }} /> M-PESA
            </div>
          </div>
        </div>

        {/* RIGHT — the CTA, then the skeleton while Paystack opens */}
        <div style={{ background: '#fff', padding: '46px clamp(20px,5vw,64px)', display: 'grid', alignContent: 'center' }}>
          <div style={{ maxWidth: 400, width: '100%' }}>
            {!busy && (
              <>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#1b1533', marginBottom: 4 }}>{payTitle}</div>
                {payBlurb && <div style={{ fontSize: 13.5, color: '#5a5470', lineHeight: 1.55, marginBottom: 22 }}>{payBlurb}</div>}
                <button type="button" className="pss-cta" onClick={onPay}
                  style={{ background: accentGradient, boxShadow: `0 14px 30px -10px ${accent}8c` }}>
                  <FaLock style={{ fontSize: 13 }} /> {ctaLabel}
                </button>
                {footnote && <div style={{ fontSize: 12.5, color: '#9a93ae', marginTop: 18, lineHeight: 1.6 }}>{footnote}</div>}
              </>
            )}
            {busy && (
              <div aria-live="polite">
                <div style={{ fontSize: 18, fontWeight: 800, color: '#1b1533', marginBottom: 4 }}>Opening secure checkout…</div>
                <div style={{ fontSize: 13.5, color: '#9a93ae', marginBottom: 22 }}>Bringing up your card &amp; M-PESA options.</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {skelBar('100%')}
                  {skelBar('100%')}
                  <div style={{ display: 'flex', gap: 12 }}>{skelBar('50%')}{skelBar('50%')}</div>
                  <div className="pss-skel" style={{ height: 48, width: '100%', borderRadius: 12, marginTop: 8 }} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
